import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React, { useCallback, useMemo } from 'react';
import {
    IExploreActionsLocale,
    IExploreFilterLocale,
} from 'src/features/common/explore/containers/explore-filter-container/types';
import { IExploreFilters } from 'src/features/common/explore/ducks/explore-nfts/explore-nfts.types';
import { useFilters, useSetFilters } from 'src/features/common/explore/hooks/use-explore-nfts';
import FilterBlockchain from 'src/features/white-labels/ocean/explore/components/explore-nft-filter-items/filter-blockchain';
import { FilterPriceRange } from 'src/features/white-labels/ocean/explore/components/explore-nft-filter-items/filter-price-range';
import FilterQuantity from 'src/features/white-labels/ocean/explore/components/explore-nft-filter-items/filter-quantity';
import FilterSalesType from 'src/features/white-labels/ocean/explore/components/explore-nft-filter-items/filter-sales-type';
import { CategoriesFilterContainer } from 'src/features/white-labels/ocean/explore/containers/entity-filter-container/categories-filter-container';
import { CollectionsFilterContainer } from 'src/features/white-labels/ocean/explore/containers/entity-filter-container/collections-filter-container';
import { CountriesFilterContainer } from 'src/features/white-labels/ocean/explore/containers/entity-filter-container/countries-filter-container';
import { FilterItem } from './filter-item';

export const ExploreFilterContainer = () => {
    const router = useRouter();
    const hideCollections = router.asPath?.includes('/collections');
    const hideCountry = router.asPath?.includes('/creator');
    const { t } = useTranslation(['components']);
    const filters = useFilters();
    const setFilters = useSetFilters();

    const handleFiltersChange = useCallback(
        (type: keyof IExploreFilters, value: any) => {
            setFilters({
                ...filters,
                [type]: value,
            });
        },
        [filters, setFilters]
    );

    const locale: IExploreFilterLocale = useMemo(
        () => ({
            ...t('exploreFilter.filter', { ns: 'components', returnObjects: true }),
        }),
        [t]
    );

    const actionsLocale: IExploreActionsLocale = useMemo(
        () => t('exploreFilter.actions', { ns: 'components', returnObjects: true }),
        [t]
    );

    return (
        <>
            <FilterItem title={locale.saleType.label} isActive={!!filters.sellType?.length}>
                <FilterSalesType locale={locale.saleType} selected={filters.sellType!} onChange={handleFiltersChange} />
            </FilterItem>
            <FilterItem title={locale.quantity.label} isActive={!!filters.standard?.length}>
                <FilterQuantity locale={locale.quantity} selected={filters.standard!} onChange={handleFiltersChange} />
            </FilterItem>
            <FilterItem title={locale.blockchains.label} isActive={!!filters.blockchainId?.length}>
                <FilterBlockchain selected={filters.blockchainId!} onChange={handleFiltersChange} />
            </FilterItem>
            <FilterItem
                title={locale.priceRange.label}
                isActive={!!filters.priceRange?.from || !!filters.priceRange?.to}
            >
                <FilterPriceRange
                    locale={locale.priceRange}
                    actionsLocale={actionsLocale}
                    values={filters.priceRange!}
                    onChange={handleFiltersChange}
                />
            </FilterItem>
            <FilterItem title={locale.categories.label} isActive={!!filters.categoryId?.length}>
                <CategoriesFilterContainer selected={filters.categoryId!} onChange={handleFiltersChange} />
            </FilterItem>
            {!hideCollections && (
                <FilterItem title={locale.collections.label} isActive={!!filters.collectionId?.length}>
                    <CollectionsFilterContainer selected={filters.collectionId!} onChange={handleFiltersChange} />
                </FilterItem>
            )}
            {!hideCountry && (
                <FilterItem title={locale.country.label} isActive={!!filters.artistCountry?.length}>
                    <CountriesFilterContainer selected={filters.artistCountry!} onChange={handleFiltersChange} />
                </FilterItem>
            )}
        </>
    );
};
